import React, { useContext, useEffect, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Grid
} from '@mui/material';
import { doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

import { companiesRef } from '../../api/firebase/firestore/company';
import { usersRef } from '../../api/firebase/firestore/user';
import {
  black,
  blackText,
  blue,
  blueButton,
  cardBorder,
  tertiaryButtonBackground
} from '../../constants/Colors';
import {
  genreList,
  purpose as purposeList,
  budget as budgetList,
  mediaTypes as mediaTypeList,
  menuList
} from '../../constants/Select';
import { screenType } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';

import CustomDatePicker from './CustomDatePicker';
import CustomSelectField from './CustomSelectField';

import type { List } from './CustomSelectField';
import type { Dayjs } from 'dayjs';

const styles = {
  blueButton,
  button: {
    boxShadow: 0,
    height: '36px',
    minWidth: '148px',
    width: '192px'
  },
  buttonAndChips: {
    columnGap: '8px',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: '8px'
  },
  buttons: {
    '@media screen and (max-width:960px)': {
      justifyContent: 'center',
      padding: '24px 16px'
    },
    columnGap: '16px',
    rowGap: '16px'
  },
  buttonsInner: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  card: {
    '@media screen and (max-width:480px)': {
      margin: '16px'
    },
    boxShadow: 0,
    margin: '24px'
  },
  cardBorder,
  cardContents: {
    '@media screen and (max-width:960px)': {
      border: '1px solid #222',
      margin: 0
    },
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
    margin: '24px'
  },
  cardPcContainer: {
    '@media screen and (max-width:1350px)': {
      display: 'none'
    }
  },
  chips: {
    columnGap: '8px',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: '8px'
  },
  clear: {
    backGroundColor: 'white',
    border: `1px solid ${black}`,
    color: blackText,
    marginRight: '16px',
    width: '200px'
  },
  clearButton: {
    color: black
  },
  columnNameLeft: {
    backgroundColor: '#F2F2F2',
    borderBottom: '1px solid #DBDBE0',
    borderLeft: '1px solid #DBDBE0',
    boxSizing: 'border-box',
    color: black,
    fontSize: '14px',
    fontWeight: 'bold',
    maxWidth: '148px',
    padding: '20px 24px',
    width: '100%'
  },
  columnNameRight: {
    backgroundColor: '#F2F2F2',
    borderBottom: '1px solid #DBDBE0',
    boxSizing: 'border-box',
    color: black,
    fontSize: '14px',
    fontWeight: 'bold',
    maxWidth: '148px',
    padding: '20px 24px',
    width: '100%'
  },
  date: {
    ml: '24px',
    mt: '4px',
    width: '65%'
  },
  dateField: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  dateMargin: {
    margin: '12px 6px 0'
  },
  demoTableBorder: {
    '@media screen and (max-width:1350px)': {
      border: 'none',
      p: '12px 24px 0'
    },
    '@media screen and (max-width:700px)': {
      flexDirection: 'column',
      p: '12px 16px 0'
    },
    border: '1px solid #DBDBE0'
  },
  demoTableContent: {
    '@media screen and (max-width:700px)': {
      width: '100%'
    },
    height: '60px'
  },
  demoTableOption: {
    width: '120px'
  },
  demoTablePadding: {
    p: '12px 24px'
  },
  demoTablePercentBetween: {
    '@media screen and (max-width:700px)': {
      p: '6px 4px'
    },
    p: '6px 14px'
  },
  demoTablePercents: {
    '@media screen and (max-width:700px)': {
      p: '12px 16px'
    },
    p: '12px 24px'
  },
  demoTableText: {
    '@media screen and (max-width:1200px)': {
      whiteSpace: 'normal'
    },
    fontSize: '14px',
    fontWeight: '700',
    pl: 0,
    whiteSpace: 'pre-wrap'
  },
  demoTableTextbg: {
    '@media screen and (max-width:700px)': {
      height: '44px',
      width: '100%'
    },
    bgcolor: '#F2F2F2',
    width: '140px'
  },
  demoTableWidth: {
    '@media screen and (max-width:700px)': {
      width: '311px'
    },
    width: '596px'
  },
  divider: {
    color: '#DBDBE0',
    pt: '24px'
  },
  field: {
    borderBottom: '1px solid #DBDBE0',
    padding: '12px 24px',
    width: '100%'
  },
  fieldRight: {
    borderBottom: '1px solid #DBDBE0',
    borderRight: '1px solid #DBDBE0',
    padding: '12px 24px',
    width: '100%'
  },
  marginField: {
    borderBottom: '1px solid #DBDBE0',
    borderRight: '1px solid #DBDBE0',
    maxWidth: '644px',
    width: '100%'
  },
  searchBox: {
    '@media screen and (max-width:960px)': {
      rowGap: 0
    },
    flexDirection: 'column',
    rowGap: '24px'
  },
  searchButtons: {
    paddingTop: '24px'
  },
  searchSpButton: {
    '@media screen and (max-width:960px)': {
      display: 'flex'
    },
    bgcolor: '#F2F2F2',
    color: black,
    display: 'none',
    flexDirection: 'row',
    height: '48px',
    justifyContent: 'space-between',
    padding: '12px 16px',
    textAlign: 'left'
  },
  searchTable: {
    display: 'block'
  },
  table: {
    display: 'flex',
    whiteSpace: 'pre-line'
  },
  tableButton: {
    color: blue,
    mr: '16px',
    padding: '0px'
  },
  tableItem: {
    '@media screen and (max-width:960px)': {
      bgcolor: '#FFF',
      padding: 0
    },
    bgcolor: '#F2F2F2',
    padding: '18px 24px',
    width: '195px'
  },
  tableRow: {
    '@media screen and (max-width:960px)': {
      flexDirection: 'column',
      padding: '12px 16px',
      rowGap: '8px'
    },
    border: '1px solid #E5E5E5',
    flexDirection: 'row',
    padding: 0,
    rowGap: 0
  },
  tableValue: {
    '@media screen and (max-width:960px)': {
      padding: 0
    },
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    padding: '12px 24px'
  },
  tertiaryButtonBackground,
  text: {
    fontSize: '14px',
    mb: '8px'
  },
  textfield: {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        borderColor: 'inherit'
      },
      '&:hover fieldset': {
        borderColor: 'inherit'
      }
    },
    '@media screen and (max-width:960px)': {
      mt: '4px',
      pb: '24px'
    }
  },
  topColumnNameLeft: {
    backgroundColor: '#F2F2F2',
    borderBottom: '1px solid #DBDBE0',
    borderLeft: '1px solid #DBDBE0',
    borderTop: '1px solid #DBDBE0',
    boxSizing: 'border-box',
    color: black,
    fontSize: '14px',
    fontWeight: 'bold',
    maxWidth: '148px',
    padding: '20px 24px',
    width: '100%'
  },
  topColumnNameRight: {
    backgroundColor: '#F2F2F2',
    borderBottom: '1px solid #DBDBE0',
    borderTop: '1px solid #DBDBE0',
    boxSizing: 'border-box',
    color: black,
    fontSize: '14px',
    fontWeight: 'bold',
    maxWidth: '148px',
    padding: '20px 24px',
    width: '100%'
  },
  topFieldLeft: {
    borderBottom: '1px solid #DBDBE0',
    borderTop: '1px solid #DBDBE0',
    padding: '12px 24px',
    width: '100%'
  },
  topFieldRight: {
    borderBottom: '1px solid #DBDBE0',
    borderRight: '1px solid #DBDBE0',
    borderTop: '1px solid #DBDBE0',
    padding: '12px 24px',
    width: '100%'
  }
};

interface Props {
  handleChange: (
    productName: string,
    startDate: { start: Dayjs | null; end: Dayjs | null },
    applicationDate: { start: Dayjs | null; end: Dayjs | null },
    genre: string | number | null,
    budget: string | number | null,
    purpose: string | number | null,
    mediaType: string | number | null,
    menu: number | null,
    client: string | number | null
  ) => void;
  screen: number;
}
const ClientAgentIssueSearchCard = (props: Props): React.ReactElement => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const { handleChange, screen } = props;

  const genres = genreList();
  const purposes = purposeList();
  const budgets = budgetList();
  const mediaTypes = mediaTypeList();
  const menus = menuList();
  const [clientList, setClientList] = useState<List[]>([]);

  // 代理店の場合、クライアントのリストを取得する
  useEffect(() => {
    void (async () => {
      try {
        if (screen === screenType.agent && currentUser) {
          const userRef = doc(usersRef, currentUser.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const ownCampanyRef = doc(
              companiesRef,
              userSnap.data()?.companyId as string
            );
            const companySnap = await getDoc(ownCampanyRef);

            const companies = query(
              companiesRef,
              where('agentFlg', '==', true),
              where('agentLists', '==', [{ companyId: companySnap.id }])
            );
            const companiesSnapshots = await getDocs(companies);
            const companyList = companiesSnapshots.docs.map((document) => ({
              option: document.data().name as string,
              value: document.id
            }));
            setClientList((prev) => [...prev, ...companyList]);
          }
        }
      } catch (e) {
        throwError(e);
      }
    })();
  }, []);

  const [mediaType, setMediaType] = useState<string | number | null>(0);
  const [menu, setMenu] = useState<number | null>(0);
  const [budget, setBudget] = useState<string | number | null>(0);
  const [purpose, setPurpose] = useState<string | number | null>(0);
  const [genre, setGenre] = useState<string | number | null>(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [productName, setProductName] = useState<string>('');
  const [startDate, setStartDate] = useState<{
    start: Dayjs | null;
    end: Dayjs | null;
  }>({
    end: null,
    start: null
  });
  const [applicationDate, setApplicationDate] = useState<{
    start: Dayjs | null;
    end: Dayjs | null;
  }>({
    end: null,
    start: null
  });
  const [client, setClient] = useState<string | number | null>(0);

  const handleExChange = (): void => {
    setIsExpanded(!isExpanded);
  };
  const handleProductNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setProductName(event.target.value);
  };

  const resetSeatchParams = (): void => {
    setProductName('');
    setStartDate({
      end: null,
      start: null
    });
    setApplicationDate({
      end: null,
      start: null
    });
    setGenre(0);
    setBudget(0);
    setPurpose(0);
    setMediaType(0);
    setMenu(0);
    setClient(0);
  };

  return (
    <>
      {/* SP */}
      <Typography
        component="div"
        sx={{
          '@media screen and (max-width: 480px)': {
            margin: '16px'
          },
          backgroundColor: '#F3F3F3',
          margin: '16px 24px'
        }}
      >
        <Accordion
          sx={[
            {
              '@media screen and (min-width:1350px)': {
                display: 'none'
              },
              border: '1px solid #222',
              boxShadow: 'none'
            }
          ]}
          onChange={() => handleExChange()}
          expanded={isExpanded}
        >
          <AccordionSummary
            expandIcon={isExpanded ? <RemoveIcon /> : <AddIcon />}
            sx={{
              backgroundColor: isExpanded ? '#F3F3F3' : 'white'
            }}
          >
            <Typography>{t('media.searchBlock')}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Grid container>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid item sx={styles.demoTableContent} container>
                  <Typography component="p" sx={styles.demoTableText}>
                    {t('orientation.product')}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    sx={styles.textfield}
                    placeholder={t('media.searchFreeWordPlaceholder')}
                    value={productName}
                    onChange={handleProductNameChange}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Divider sx={styles.divider} />
            <Grid container>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid item sx={styles.demoTableContent} container>
                  <Grid item xs={12}>
                    <Typography component="p" sx={styles.demoTableText}>
                      {t('issue.genre')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <CustomSelectField
                      blank
                      value={genre}
                      isRequired={false}
                      width={'search'}
                      list={genres}
                      onChange={(e) =>
                        setGenre(e.target.value as string | null)
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Divider sx={styles.divider} />
            <Grid container>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid item sx={styles.demoTableContent} container>
                  <Grid item xs={12}>
                    <Typography component="p" sx={styles.demoTableText}>
                      {t('issue.purpose')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <CustomSelectField
                      blank
                      value={purpose}
                      isRequired={false}
                      width={'search'}
                      list={purposes}
                      onChange={(e) =>
                        setPurpose(e.target.value as string | null)
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Divider sx={styles.divider} />

            <Grid container>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid item sx={styles.demoTableContent} container>
                  <Grid xs={12}>
                    <Typography component="p" sx={styles.demoTableText}>
                      {t('issue.startDate')}
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <CustomDatePicker
                      isRequired={false}
                      placeholder="2022/08/24"
                      width={'100%'}
                      value={startDate.start}
                      setValue={(e) => setStartDate({ ...startDate, start: e })}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Typography
                      sx={[
                        styles.demoTableText,
                        { pt: '12px', textAlign: 'center' }
                      ]}
                      component="p"
                    >
                      〜
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <CustomDatePicker
                      isRequired={false}
                      width={'100%'}
                      error={false}
                      placeholder="2022/08/24"
                      value={startDate.end}
                      setValue={(e) => setStartDate({ ...startDate, end: e })}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Divider sx={styles.divider} />

            <Grid container>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid item sx={styles.demoTableContent} container>
                  <Grid xs={12}>
                    <Typography component="p" sx={styles.demoTableText}>
                      {t('issue.applicationDate')}
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <CustomDatePicker
                      isRequired={false}
                      placeholder="2022/08/24"
                      width={'100%'}
                      value={applicationDate.start}
                      setValue={(e) =>
                        setApplicationDate({ ...applicationDate, start: e })
                      }
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <Typography
                      sx={[
                        styles.demoTableText,
                        { pt: '12px', textAlign: 'center' }
                      ]}
                      component="p"
                    >
                      〜
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <CustomDatePicker
                      isRequired={false}
                      width={'100%'}
                      error={false}
                      placeholder="2022/08/24"
                      value={applicationDate.end}
                      setValue={(e) =>
                        setApplicationDate({ ...applicationDate, end: e })
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Divider sx={styles.divider} />
            <Grid container>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid item sx={styles.demoTableContent} container>
                  <Grid item xs={12}>
                    <Typography component="p" sx={styles.demoTableText}>
                      {t('issue.budget')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <CustomSelectField
                      blank
                      value={budget}
                      onChange={(e) =>
                        setBudget(e.target.value as string | null)
                      }
                      isRequired={false}
                      width={'search'}
                      list={budgets}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Divider sx={styles.divider} />
            <Grid container>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid item sx={styles.demoTableContent} container>
                  <Grid item xs={12}>
                    <Typography component="p" sx={styles.demoTableText}>
                      {t('issue.mediaTypeSimple')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <CustomSelectField
                      blank
                      value={mediaType}
                      onChange={(e) =>
                        setMediaType(e.target.value as string | null)
                      }
                      isRequired={false}
                      width={'search'}
                      list={mediaTypes}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Divider sx={styles.divider} />
            <Grid container>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid item sx={styles.demoTableContent} container>
                  <Grid item xs={12}>
                    <Typography component="p" sx={styles.demoTableText}>
                      {t('issue.advertismentMenu')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <CustomSelectField
                      blank
                      value={menu}
                      onChange={(e) => setMenu(e.target.value as number | null)}
                      isRequired={false}
                      width={'search'}
                      list={menus}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {screen === screenType.agent && (
              <>
                <Divider sx={styles.divider} />
                <Grid container>
                  <Grid item xs={12} sx={styles.demoTableBorder} container>
                    <Grid item sx={styles.demoTableContent} container>
                      <Grid item xs={12}>
                        <Typography component="p" sx={styles.demoTableText}>
                          {t('type.client')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <CustomSelectField
                          blank
                          value={client}
                          onChange={(e) =>
                            setClient(e.target.value as number | null)
                          }
                          isRequired={false}
                          width={'search'}
                          list={clientList}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}

            <Divider sx={styles.divider} />
            <Grid container sx={{ pb: '12px' }}>
              <Grid item xs={12} sx={styles.demoTableBorder} container>
                <Grid item container>
                  <Grid item xs={6} justifyContent={'center'}>
                    <Button
                      onClick={() => {
                        resetSeatchParams();
                        handleChange(
                          '',
                          { end: null, start: null },
                          { end: null, start: null },
                          0,
                          0,
                          0,
                          0,
                          0,
                          0
                        );
                      }}
                      sx={tertiaryButtonBackground}
                      fullWidth
                    >
                      <Typography sx={[blackText, { fontSize: '14px' }]}>
                        {t('issue.clear')}
                      </Typography>
                    </Button>
                  </Grid>
                  <Grid item xs={6} justifyContent={'center'}>
                    <Button
                      sx={[
                        blueButton,
                        { fontSize: '14px', ml: '10%', width: '90%' }
                      ]}
                      onClick={() =>
                        handleChange(
                          productName,
                          startDate,
                          applicationDate,
                          genre,
                          budget,
                          purpose,
                          mediaType,
                          menu,
                          client
                        )
                      }
                    >
                      {t('issue.search')}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Typography>

      {/* PC */}
      <Card sx={[styles.card, styles.cardPcContainer]}>
        <CardContent>
          <Typography component="div" sx={styles.searchTable}>
            <Typography component="div" sx={styles.table}>
              <Typography sx={styles.topColumnNameLeft}>
                {t('form.productName')}
              </Typography>
              <Typography component="div" sx={styles.topFieldLeft}>
                <TextField
                  size="small"
                  placeholder={t('media.searchFreeWordPlaceholder')}
                  fullWidth
                  value={productName}
                  onChange={handleProductNameChange}
                  sx={styles.textfield}
                />
              </Typography>
              <Typography sx={styles.topColumnNameRight}>
                {t('issue.applicationDate')}
              </Typography>
              <Typography component="div" sx={styles.topFieldRight}>
                <Typography component="div" sx={styles.dateField}>
                  <CustomDatePicker
                    isRequired={false}
                    width={'100%'}
                    error={false}
                    placeholder="2022/08/24"
                    value={applicationDate.start}
                    formMb={'zero'}
                    setValue={(e) =>
                      setApplicationDate({ ...applicationDate, start: e })
                    }
                    flex={1}
                  />
                  <Typography sx={styles.dateMargin}> 〜 </Typography>
                  <CustomDatePicker
                    isRequired={false}
                    width={'100%'}
                    error={false}
                    placeholder="2022/08/24"
                    value={applicationDate.end}
                    formMb={'zero'}
                    setValue={(e) =>
                      setApplicationDate({ ...applicationDate, end: e })
                    }
                    flex={1}
                  />
                </Typography>
              </Typography>
            </Typography>
            <Typography component="div" sx={styles.table}>
              <Typography sx={styles.columnNameLeft}>
                {t('issue.genre')}
              </Typography>
              <Typography component="div" sx={styles.field}>
                <CustomSelectField
                  blank
                  value={genre}
                  isRequired={false}
                  list={genres}
                  onChange={(e) => setGenre(e.target.value as string | null)}
                  width={'search'}
                  formMb={'zero'}
                />
              </Typography>
              <Typography sx={styles.columnNameRight}>
                {t('issue.budget')}
              </Typography>
              <Typography component="div" sx={styles.fieldRight}>
                <CustomSelectField
                  blank
                  value={budget}
                  onChange={(e) => setBudget(e.target.value as string | null)}
                  isRequired={false}
                  width={'search'}
                  formMb={'zero'}
                  list={budgets}
                />
              </Typography>
            </Typography>
            <Typography component="div" sx={styles.table}>
              <Typography sx={styles.columnNameLeft}>
                {t('issue.purpose')}
              </Typography>
              <Typography component="div" sx={styles.field}>
                <CustomSelectField
                  blank
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value as string | null)}
                  isRequired={false}
                  width={'search'}
                  formMb={'zero'}
                  list={purposes}
                />
              </Typography>
              <Typography sx={styles.columnNameRight}>
                {t('issue.mediaTypeTwoLine')}
              </Typography>
              <Typography component="div" sx={styles.fieldRight}>
                <CustomSelectField
                  blank
                  value={mediaType}
                  onChange={(e) =>
                    setMediaType(e.target.value as string | null)
                  }
                  isRequired={false}
                  width={'search'}
                  formMb={'zero'}
                  list={mediaTypes}
                />
              </Typography>
            </Typography>
            <Typography component="div" sx={styles.table}>
              <Typography sx={styles.columnNameLeft}>
                {t('issue.startDate')}
              </Typography>
              <Typography component="div" sx={styles.field}>
                <Typography component="div" sx={styles.dateField}>
                  <CustomDatePicker
                    isRequired={false}
                    width={'100%'}
                    error={false}
                    placeholder="2022/08/24"
                    value={startDate.start}
                    formMb={'zero'}
                    setValue={(e) => setStartDate({ ...startDate, start: e })}
                    flex={1}
                  />
                  <Typography sx={styles.dateMargin}> 〜 </Typography>
                  <CustomDatePicker
                    isRequired={false}
                    width={'100%'}
                    error={false}
                    placeholder="2022/08/24"
                    value={startDate.end}
                    formMb={'zero'}
                    setValue={(e) => setStartDate({ ...startDate, end: e })}
                    flex={1}
                  />
                </Typography>
              </Typography>
              <Typography component="div" sx={styles.columnNameRight}>
                {t('issue.advertismentMenu')}
              </Typography>
              <Typography component="div" sx={styles.fieldRight}>
                <CustomSelectField
                  blank
                  value={menu}
                  onChange={(e) => setMenu(e.target.value as number | null)}
                  isRequired={false}
                  width={'search'}
                  formMb={'zero'}
                  list={menus}
                />
              </Typography>
            </Typography>
            {screen === screenType.agent && (
              <Typography component="div" sx={styles.table}>
                <Typography sx={styles.columnNameLeft}>
                  {t('type.client')}
                </Typography>
                <Typography component="div" sx={styles.field}>
                  <CustomSelectField
                    blank
                    value={client}
                    onChange={(e) => setClient(e.target.value as string | null)}
                    isRequired={false}
                    width={'search'}
                    formMb={'zero'}
                    list={clientList}
                  />
                </Typography>
                <Typography sx={styles.columnNameRight}></Typography>
                <Typography sx={styles.fieldRight}></Typography>
              </Typography>
            )}
          </Typography>
          <Typography component={'div'} sx={styles.searchButtons}>
            <Typography component={'div'} sx={styles.buttonsInner}>
              <Button
                onClick={() => {
                  resetSeatchParams();
                  handleChange(
                    '',
                    { end: null, start: null },
                    { end: null, start: null },
                    0,
                    0,
                    0,
                    0,
                    0,
                    0
                  );
                }}
                fullWidth
                sx={styles.clear}
              >
                {t('issue.clear')}
              </Button>
              <Button
                fullWidth
                sx={[styles.blueButton, styles.button]}
                onClick={() =>
                  handleChange(
                    productName,
                    startDate,
                    applicationDate,
                    genre,
                    budget,
                    purpose,
                    mediaType,
                    menu,
                    client
                  )
                }
              >
                {t('issue.search')}
              </Button>
            </Typography>
          </Typography>
        </CardContent>
      </Card>
    </>
  );
};
export default ClientAgentIssueSearchCard;
